// |-- Built-in Dependencies --|
import React, { useState, useEffect } from "react";
import { FormikProps } from "formik";

// |-- Internal Dependencies --|

import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import ATMTextArea from "src/components/UI/atoms/formFields/ATMTextArea/ATMTextArea";
import useBranches from "src/hooks/useGetBranches";
import { useGetAllAccountsQuery } from "src/services/AccountServices";
import { useGetAllExpenseCategoryQuery } from "src/services/ExpenseCategoryServices";
import { handleValidNumber } from "src/utils/validations/numberInput";
import ATMCheckbox from "src/components/UI/atoms/formFields/ATMCheckbox/ATMCheckbox";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import { ExpenseFormValues } from 'src/models/Expense.model';

// |-- Types --|
type Props = {
  onClose: () => void;
  formikProps: FormikProps<ExpenseFormValues>;
  formType?: string
};

const ExpenseForm = ({ onClose, formikProps, formType }: Props) => {
  const [accountOption, setAccountOption] = useState<any>([]);
  const [categoryOption, setCategoryOption] = useState<any>([]);

  const { values, setFieldValue, handleBlur, isSubmitting, handleSubmit } = formikProps;

  // for account option
  const {
    isLoading: isAccoutnLoading,
    isFetching: isAccountFetching,
    data: accountData,
  } = useGetAllAccountsQuery("");

  useEffect(() => {
    if (!isAccoutnLoading && !isAccountFetching) {
      const filteredAccount = accountData?.data?.map((ele: any) => {
        return {
          label: ele?.accountName,
          value: ele?._id,
        };
      });
      setAccountOption(filteredAccount);
    }
  }, [isAccoutnLoading, isAccountFetching, accountData]);

  //   Get All Branched
  const { branches, isDataLoading: isBranchLoading } = useBranches();

  // for category options
  const {
    isLoading: isCategoryLoading,
    isFetching: isCategoryFetching,
    data: categoryData,
  } = useGetAllExpenseCategoryQuery("");

  useEffect(() => {
    if (!isCategoryLoading && !isCategoryFetching) {
      const filteredAccount = categoryData?.data?.map((ele: any) => {
        return {
          label: ele?.categoryName,
          value: ele?._id,
        };
      });
      setCategoryOption(filteredAccount);
    }
  }, [isCategoryLoading, isCategoryFetching, categoryData]);

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium"> {formType} Expenses </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>
      <div className="flex flex-col gap-4">
        <ATMTextField
          name="amount"
          value={values.amount}
          placeholder="Enter amount"
          label="Amount"
          onBlur={handleBlur}
          onChange={(e) => {
             setFieldValue("amount", e.target.value);
          }}
        />

        <ATMSelect
          name="categoryId"
          value={values.categoryId}
          label="Category"
          placeholder="Select Category"
          isLoading={isCategoryLoading}
          options={categoryOption}
          onChange={(newValue) => setFieldValue("categoryId", newValue)}
          onBlur={handleBlur}
        />

        <ATMSelect
          name="accountId"
          value={values.accountId}
          label="Account"
          placeholder="Select account"
          isLoading={isAccoutnLoading}
          options={accountOption}
          onChange={(newValue) => setFieldValue("accountId", newValue)}
          onBlur={handleBlur}
        />

        <ATMSelect
          name="branchId"
          value={values.branchId}
          label="Branch"
          placeholder="Select branch"
          isLoading={isBranchLoading}
          options={branches?.map((branch: any) => ({
            label: branch?.branchName,
            value: branch?._id,
          }))}
          onChange={(newValue) => setFieldValue("branchId", newValue)}
          onBlur={handleBlur}
        />
 <div>
              <ATMDatePicker
                name="expense_on_date"
                value={values?.expense_on_date || null}
                onChange={(newValue) => setFieldValue("expense_on_date", newValue)}
                label="Expense On Date"
              />
            </div>
        <ATMTextArea
          name="remark"
          value={values.remark}
          onChange={(value) => setFieldValue("remark", value)}
          label="Remark"
          placeholder="Enter remark"
          onBlur={handleBlur}
        /> 
        

        <ATMCheckbox
          extraClasses="font-semibold mt-2"
          checked={values?.will_return}
          onChange={(newValue) => {
            setFieldValue("will_return", newValue);
            setFieldValue("return_amount", "");
            setFieldValue("return_date", "");
            setFieldValue("return_remark", "");
          }}
          label="Will Return "
        ></ATMCheckbox>

        {values?.will_return ? (
          <div className="flex flex-col gap-4">
            {/* Return amount */}
            <div className="">
              <ATMTextField
                name="return_amount"
                value={values.return_amount}
                onChange={(e) => { handleValidNumber(e) && setFieldValue("return_amount", e.target.value) }}
                label="Return Amount"
                placeholder="Enter return amount"
                onBlur={handleBlur}
              />
            </div>
            <div>
              <ATMDatePicker
                name="return_date"
                value={values.return_date || null}
                onChange={(newValue) => setFieldValue("return_date", newValue)}
                label="Return Date"
              />
            </div>
            {/* Return Remark */}
            <div className="">
              <ATMTextField
                name="return_remark"
                value={values.return_remark}
                onChange={(e) => setFieldValue("return_remark", e.target.value)}
                label="Return Remark"
                placeholder="Enter return remark"
                onBlur={handleBlur}
              />
            </div>
          </div>
        ) : null}
      </div>
      <div>
        <ATMLoadingButton
          isLoading={isSubmitting}
          type="submit"
          onClick={() => handleSubmit()}
        >
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default ExpenseForm;
